import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, setToken } from '../lib/api';
import { useI18n } from '../lib/i18n';

export default function AuthPage() {
  const nav = useNavigate();
  const { t, lang, setLang } = useI18n();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** null = not answered yet; only asked on register */
  const [hasWeex, setHasWeex] = useState<boolean | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'register' && hasWeex === null) {
      setError(t.authWeexAsk);
      return;
    }
    setBusy(true);
    setError('');
    const em = email.trim().toLowerCase();
    try {
      const data = await api<{ token: string; user?: { isAdmin?: boolean; email?: string } }>(`/api/auth/${mode}`, {
        method: 'POST',
        body: JSON.stringify({ email: em, password }),
      });
      setToken(data.token);
      if (mode === 'login' && data.user?.isAdmin) {
        try {
          const adm = await api<{ token: string }>('/api/admin/login', {
            method: 'POST',
            body: JSON.stringify({ email: em, password }),
          });
          localStorage.setItem('termix_admin_token', adm.token);
        } catch { /* desk session still valid */ }
        nav('/admin');
        return;
      }
      nav('/');
    } catch (err) {
      const raw = (err as Error).message || '';
      if (mode === 'login' && /invalid credentials/i.test(raw) && em.includes('admin@')) {
        try {
          const adm = await api<{ token: string }>('/api/admin/login', {
            method: 'POST',
            body: JSON.stringify({ email: em, password }),
          });
          localStorage.setItem('termix_admin_token', adm.token);
          nav('/admin');
          return;
        } catch { /* fall through */ }
      }
      setError(/invalid credentials/i.test(raw) ? t.authBadCreds : raw);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', display: 'grid', placeItems: 'center', padding: 24 }}>
      <form onSubmit={submit} style={{
        width: 'min(420px, 100%)', background: 'var(--panel)', border: '1px solid var(--line)',
        borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <div style={{ fontFamily: 'var(--display)', fontSize: 42, color: 'var(--gold)' }}>Termix</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {(['en', 'ru'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  border: `1px solid ${lang === code ? 'var(--gold)' : 'var(--line)'}`,
                  color: lang === code ? 'var(--gold)' : 'var(--muted)',
                  background: 'transparent',
                }}
              >
                {code === 'en' ? t.langEn : t.langRu}
              </button>
            ))}
          </div>
        </div>
        <div style={{ color: 'var(--muted)', marginBottom: 12 }}>{t.authTagline}</div>
        <div style={{
          marginBottom: 20, padding: '10px 12px', borderRadius: 10,
          border: '1px solid var(--gold)', background: 'rgba(223,184,58,0.07)',
          fontSize: 13, lineHeight: 1.45, color: 'var(--text)',
        }}>
          {t.authHelpLink}{' '}
          <Link to="/help" style={{ color: 'var(--gold)' }}>termix.battletoads.top/help</Link>
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>
            {t.authPlansLine}
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)' }}>{t.authEmail}</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
          style={inputStyle} placeholder="you@email.com" />
        <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>{t.authPassword}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          style={inputStyle} placeholder={t.authPasswordPh} />

        {mode === 'register' ? (
          <div style={{
            marginTop: 16, padding: '10px 12px', borderRadius: 10,
            border: `1px solid ${hasWeex === null && error ? 'var(--short)' : 'var(--line)'}`,
            background: 'var(--panel-2)',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>{t.authWeexAsk}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {([
                [true, t.authWeexYes],
                [false, t.authWeexNo],
              ] as const).map(([val, label]) => (
                <button
                  key={String(val)}
                  type="button"
                  onClick={() => { setHasWeex(val); setError(''); }}
                  style={{
                    flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    border: `1px solid ${hasWeex === val ? 'var(--gold)' : 'var(--line)'}`,
                    color: hasWeex === val ? 'var(--gold)' : 'var(--text)',
                    background: hasWeex === val ? 'rgba(201,162,39,0.1)' : 'var(--panel)',
                    fontWeight: hasWeex === val ? 700 : 500,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {hasWeex === false ? (
              <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.45, color: 'var(--muted)' }}>
                <div>{t.authWeexRefLead}</div>
                <a
                  href={t.weexRefUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block', marginTop: 8, color: 'var(--gold)', fontWeight: 650,
                    wordBreak: 'break-all',
                  }}
                >
                  {t.authWeexRefCta}
                </a>
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <div style={{ color: 'var(--short)', marginTop: 12, fontSize: 14 }}>{error}</div> : null}
        <button disabled={busy} type="submit" style={{
          marginTop: 20, width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none',
          background: 'linear-gradient(180deg, #dfb83a, var(--gold))', color: '#111', fontWeight: 700, cursor: 'pointer',
        }}>{busy ? '…' : mode === 'login' ? t.authLogin : t.authRegister}</button>
        <button type="button" onClick={() => {
          setMode(mode === 'login' ? 'register' : 'login');
          setHasWeex(null);
          setError('');
        }}
          style={{ marginTop: 12, width: '100%', background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer' }}>
          {mode === 'login' ? t.authSwitchRegister : t.authSwitchLogin}
        </button>
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13 }}>
          <Link to="/admin" style={{ color: 'var(--gold)' }}>{t.authAdminLink}</Link>
        </div>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Link to="/privacy" style={{ color: 'var(--muted)' }}>Privacy</Link>
          <Link to="/terms" style={{ color: 'var(--muted)' }}>Terms</Link>
          <Link to="/help" style={{ color: 'var(--muted)' }}>Help</Link>
        </div>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', marginTop: 6, padding: '12px 14px', borderRadius: 10,
  border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)',
};
